import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowUp, Clock, EyeOff, GripVertical, MoreHorizontal, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableCell, TableRow } from "@/components/ui/table";
import { PricingModeBadge } from "@/domains/pricing/components/PricingModeBadge";
import { ServicePriceDisplay } from "@/domains/pricing/components/ServicePriceDisplay";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";
import type { ServiceRowData } from "../../types";

interface ServiceRowProps {
  service: ServiceRowData;
  onDelete: (service: ServiceRowData) => void;
  onArchive?: (service: ServiceRowData) => void;
  onRestore?: (service: ServiceRowData) => void;
  className?: string;
  isSearching?: boolean;
  isEditMode?: boolean;
  isDragInProgress?: boolean;
  isUpdating?: boolean;
  dragListeners?: any;
}

export function ServiceRow({
  service,
  onDelete,
  onArchive,
  onRestore,
  className,
  isSearching = false,
  isEditMode = false,
  isDragInProgress = false,
  isUpdating = false,
  dragListeners: _dragListeners = {},
}: ServiceRowProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("services");
  const currency = useSalonStore((state) => state.currentSalon?.currency);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: service.id,
    disabled: isDragInProgress || isUpdating || isSearching,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleRowClick = () => {
    navigate(`/services/${service.id}/edit`);
  };

  const _handleDelete = () => {
    onDelete(service);
  };

  const _handleArchive = () => {
    onArchive?.(service);
  };

  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);

  const handleRestore = () => {
    onRestore?.(service);
    setRestoreDialogOpen(false);
  };

  const handleArchiveConfirmed = () => {
    onArchive?.(service);
    setArchiveDialogOpen(false);
  };

  return (
    <>
      <TableRow
        ref={setNodeRef}
        style={style}
        onClick={isEditMode ? undefined : handleRowClick}
        className={cn(
          "bg-background hover:bg-muted/50 transition-colors",
          !isEditMode && "cursor-pointer",
          !service.isActive && "opacity-60 bg-muted/20",
          isDragging && "opacity-50",
          className
        )}
        {...attributes}
      >
        {/* Desktop drag handle column - indented vs category to show hierarchy */}
        {!isSearching && !isEditMode && (
          <TableCell className="w-8 p-2 hidden sm:table-cell">
            <div
              {...listeners}
              className={cn(
                "p-1 rounded transition-opacity flex items-center justify-center ml-6",
                isUpdating
                  ? "cursor-not-allowed opacity-30"
                  : "cursor-grab active:cursor-grabbing hover:bg-muted/50 opacity-60 hover:opacity-100"
              )}
              title={isUpdating ? "Updating order..." : "Drag to reorder"}
            >
              <GripVertical
                className={cn(
                  "w-4 h-4",
                  isUpdating ? "text-muted-foreground/50" : "text-muted-foreground"
                )}
              />
            </div>
          </TableCell>
        )}
        {/* Mobile Edit Mode Drag Handle - indented vs category */}
        {isEditMode && (
          <TableCell className="w-12 p-2 sm:hidden">
            <div
              {...listeners}
              className={cn(
                "flex items-center justify-center h-10 w-10 rounded ml-6",
                isUpdating
                  ? "cursor-not-allowed opacity-30"
                  : "cursor-grab active:cursor-grabbing hover:bg-muted/50 text-muted-foreground"
              )}
              title={isUpdating ? "Updating order..." : "Drag to reorder"}
            >
              <GripVertical
                className={cn(
                  "w-5 h-5",
                  isUpdating ? "text-muted-foreground/50" : "text-muted-foreground"
                )}
              />
            </div>
          </TableCell>
        )}

        {/* Service Name */}
        <TableCell className="font-medium py-3 sm:py-2">
          <div className="pl-1 flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <span className="font-medium text-base">{service.name}</span>

              {/* Mobile badges */}
              <div className="sm:hidden flex items-center gap-1.5">
                {!service.allowOnlineBooking && (
                  <Badge variant="outline" className="text-[13px] flex items-center gap-1">
                    <EyeOff className="w-3.5 h-3.5" />
                    Private
                  </Badge>
                )}
                {!service.isActive && (
                  <Badge variant="destructive" className="text-[13px]">
                    Inactive
                  </Badge>
                )}
              </div>
            </div>

            {/* Mobile info row */}
            <div className="sm:hidden flex items-center gap-5 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                <span>{service.duration} min</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Only show pricing mode badge for non-single-price pricing to reduce UI clutter */}
                {service.pricingMode !== "SINGLE_PRICE" && (
                  <PricingModeBadge mode={service.pricingMode} size="sm" />
                )}
                <ServicePriceDisplay
                  mode={service.pricingMode}
                  basePrice={service.basePrice}
                  minPrice={service.minPrice}
                  maxPrice={service.maxPrice}
                  currency={currency}
                  showBadge={false}
                  showOnlyPrice={true}
                  className="font-medium text-foreground"
                />
              </div>
            </div>
          </div>
        </TableCell>

        {/* Status badges - Desktop only */}
        <TableCell className="hidden sm:table-cell w-40">
          <div className="flex flex-wrap justify-center gap-1">
            {/* Only show pricing mode badge for non-fixed pricing to reduce UI clutter */}
            {service.pricingMode !== "SINGLE_PRICE" && (
              <PricingModeBadge mode={service.pricingMode} size="sm" />
            )}
            {!service.allowOnlineBooking && (
              <Badge
                variant="outline"
                className="text-xs flex items-center gap-1 whitespace-nowrap"
              >
                <EyeOff className="w-3 h-3" />
                Private
              </Badge>
            )}
            {!service.isActive && (
              <Badge variant="destructive" className="text-xs whitespace-nowrap">
                Inactive
              </Badge>
            )}
          </div>
        </TableCell>

        {/* Duration - Desktop only */}
        <TableCell className="hidden sm:table-cell w-28 text-center">
          <div className="flex items-center justify-center gap-1 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span>{service.duration}</span>
          </div>
        </TableCell>

        {/* Price - Desktop only */}
        <TableCell className="hidden sm:table-cell w-32 text-center">
          <ServicePriceDisplay
            mode={service.pricingMode}
            basePrice={service.basePrice}
            minPrice={service.minPrice}
            maxPrice={service.maxPrice}
            currency={currency}
            showBadge={false}
            showOnlyPrice={true}
            className="font-medium text-foreground text-sm"
          />
        </TableCell>

        {/* Actions - Hide in mobile edit mode */}
        {!isEditMode && (
          <TableCell className="w-12 sm:w-32 text-right p-2">
            <div className="flex items-center justify-end gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 w-10 sm:h-8 sm:w-8 p-0 hover:bg-accent"
                    onClick={(e) => e.stopPropagation()} // Prevent row click
                  >
                    <MoreHorizontal className="w-5 h-5 sm:w-4 sm:h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {service.isActive ? (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setArchiveDialogOpen(true);
                      }}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t("actions.archiveService")}
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setRestoreDialogOpen(true);
                      }}
                      className="text-green-600"
                    >
                      <ArrowUp className="w-4 h-4 mr-2" />
                      {t("actions.restoreService")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </TableCell>
        )}
        {/* Mobile edit mode - Delete button */}
        {isEditMode && (
          <TableCell className="w-12 text-right sm:hidden p-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                service.isActive ? setArchiveDialogOpen(true) : setRestoreDialogOpen(true);
              }}
              className={cn(
                "h-10 w-10 p-0",
                service.isActive
                  ? "text-muted-foreground hover:text-destructive"
                  : "text-muted-foreground hover:text-green-600"
              )}
            >
              {service.isActive ? <Trash2 className="w-5 h-5" /> : <ArrowUp className="w-5 h-5" />}
            </Button>
          </TableCell>
        )}
      </TableRow>

      {/* Archive Service Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm.archiveService.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirm.archiveService.description", { name: service.name })}
              <span className="block mt-2">{t("confirm.archiveService.detail")}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveConfirmed}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t("confirm.archiveService.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Service Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm.restoreService.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirm.restoreService.description", { name: service.name })}
              <span className="block mt-2">{t("confirm.restoreService.detail")}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore} className="bg-green-600 hover:bg-green-700">
              {t("confirm.restoreService.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
